import { useMemo } from 'react';

import { getIdLabel, parseIdKey } from 'helpers/ids';
import { useTranslation } from 'react-i18next';

import { useGetIdDocumentTypesQuery } from 'api/id-document-types-api';

export function useGetIdTypeOptions({ codes = [], photoIdOnly = false, provider = null } = {}) {
  const { t } = useTranslation('id_document_types');

  const { data: fetched = [], isLoading, error } = useGetIdDocumentTypesQuery();

  const options = useMemo(() => {
    // Base filters
    let idTypes = fetched;
    if (photoIdOnly) idTypes = idTypes.filter(x => x?.has_photo === true);
    if (provider === true) {
      idTypes = idTypes.filter(x => !!x?.provider);
    } else if (typeof provider === 'string') {
      idTypes = idTypes.filter(x => x?.provider === provider);
    }

    // If `codes` provided: order by `codes`, use per-code countryCode override
    if (Array.isArray(codes) && codes.length) {
      const typesByCode = new Map(idTypes.map(x => [x.code, x]));
      const result = [];

      for (const code of codes) {
        const { idType, countryCode } = parseIdKey(code);
        if (!idType) continue;

        const type = typesByCode.get(idType);
        if (!type) continue;

        result.push({
          code: code,
          value: code,
          name: getIdLabel(type.code, countryCode),
          has_photo: !!type.has_photo,
          provider: type.provider ?? null,
        });
      }

      return result;
    }

    // Otherwise: map all filtered entries (no countryCode override)
    return idTypes.map(type => ({
      code: type.code,
      value: type.code,
      name: getIdLabel(type.code, undefined),
      has_photo: !!type.has_photo,
      provider: type.provider ?? null,
    }));
  }, [fetched, provider, photoIdOnly, codes, t]);

  return { options, isLoading, error };
}
